import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Cloud, ExternalLink, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DropboxConnectCard({ authApi, language }) {
  const it = language === 'it';
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await authApi.get(`${API}/admin/dropbox/status`);
      setStatus(res.data);
    } catch {
      setStatus({ connected: false });
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const start = async () => {
    const popup = window.open('', '_blank');
    try {
      const res = await authApi.post(`${API}/admin/dropbox/oauth/start`);
      if (popup) popup.location.href = res.data.authorize_url;
      else window.location.assign(res.data.authorize_url);
      setStarted(true);
    } catch {
      popup?.close();
      toast.error(it ? 'Impossibile avviare il collegamento' : 'Could not start connection');
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await authApi.post(`${API}/admin/dropbox/oauth/finish`, { code });
      toast.success(it ? 'Dropbox collegato' : 'Dropbox connected');
      setCode('');
      setStarted(false);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || (it ? 'Collegamento fallito' : 'Connection failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cyber-card rounded-lg p-6" data-testid="dropbox-connect-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 rounded-lg bg-blue-500/10"><Cloud className="w-6 h-6 text-blue-400" /></div>
        <div>
          <h3 className="font-orbitron text-white font-semibold">{it ? 'Collegamento Dropbox' : 'Dropbox Connection'}</h3>
          <p className="font-mono text-xs text-gray-500">{it ? 'Archiviazione automatica dei dossier PDF' : 'Automatic PDF dossier archiving'}</p>
        </div>
      </div>

      <div className="mb-4 font-mono text-sm" data-testid="dropbox-status">
        {status === null ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : status.connected ? (
          <div className="flex items-start gap-2 text-neon-green">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              {it ? 'Collegato' : 'Connected'}{status.email ? ` — ${status.email}` : ''}
              {status.mode === 'access_token' && (
                <span className="block text-yellow-400 text-xs mt-1">
                  {it ? 'Token temporaneo: ricollega per un accesso che non scade' : 'Temporary token: reconnect for non-expiring access'}
                </span>
              )}
              {status.folder && <span className="block text-gray-500 text-xs mt-1">{status.folder}</span>}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-neon-red">
            <XCircle className="w-4 h-4" />
            {it ? 'Non collegato: i PDF non vengono archiviati' : 'Not connected: PDFs are not archived'}
          </div>
        )}
      </div>

      <ol className="font-mono text-xs text-gray-400 space-y-1 mb-4 list-decimal list-inside">
        <li>{it ? 'Clicca "Autorizza Dropbox" e accedi al tuo account' : 'Click "Authorize Dropbox" and sign in'}</li>
        <li>{it ? 'Premi "Consenti" e copia il codice mostrato' : 'Press "Allow" and copy the code shown'}</li>
        <li>{it ? 'Incolla il codice qui sotto e salva' : 'Paste the code below and save'}</li>
      </ol>

      <button onClick={start} data-testid="dropbox-authorize-btn"
        className="cyber-btn-outline px-4 py-2.5 flex items-center justify-center gap-2 rounded-md w-full mb-3">
        <ExternalLink className="w-4 h-4" />
        {status?.connected ? (it ? 'Ricollega Dropbox' : 'Reconnect Dropbox') : (it ? 'Autorizza Dropbox' : 'Authorize Dropbox')}
      </button>

      {(started || code) && (
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="dropbox-code-input"
            placeholder={it ? 'Incolla il codice di Dropbox' : 'Paste Dropbox code'} className="cyber-input h-11 rounded-md px-3 text-sm flex-1" />
          <button onClick={finish} disabled={!code.trim() || busy} data-testid="dropbox-save-code-btn"
            className="cyber-btn px-4 rounded-md flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {it ? 'Salva' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
